import { type JSX, memo, useEffect } from "react";

import type { MapMouseEvent } from "maplibre-gl";
import { useMap } from "react-map-gl/maplibre";

import { useThrottle } from "@/app/hooks";
import {
  type KeyHandler,
  useKeyPressListener,
} from "@/app/hooks/useKeyPressListener";
import { useThrottledDragEvent } from "@/features/editor/hooks/useThrottledDragEvent";
import type { BaseMode } from "@/features/geojson_editor/modes/BaseMode";
import useMapEventHandler from "@/features/map/hooks/events/useMapEventHandler";
import {
  LAYERID_EDITOR_CENTERPOINT,
  LAYERID_EDITOR_FILL,
  LAYERID_EDITOR_LINE,
  LAYERID_EDITOR_VERTEX,
} from "@/features/map/layers/maplibre/EditorLayer";
import {
  EventPriority,
  useMapInteractions,
} from "@/features/map/MapInteractionsProvider";
import { closePopupMenus } from "../app/components/modals/PopupMenu";

const THROTTLE_DELAY = 33; // ~30fps

export const EventListeners = memo(
  ({ mode }: { mode: BaseMode }): JSX.Element => {
    const { subscribe, unsubscribe } = useMapInteractions();
    const { map } = useMap();

    // Wrap keyboard event handlers to close previous popup menus
    const wrapKeyboardEvent = (handler: KeyHandler) => {
      return (event: KeyboardEvent) => {
        if (!handler) return;
        const result = handler(event);
        closePopupMenus();
        return result;
      };
    };

    // Create throttled event handlers at the top level
    const throttledVertexDrag = useThrottledDragEvent({
      callback: mode.eventHandlers.onVertexDrag || (() => {}),
      delay: THROTTLE_DELAY,
    }).throttledDragEvent;

    const throttledFillDrag = useThrottledDragEvent({
      callback: mode.eventHandlers.onFillDrag || (() => {}),
      delay: THROTTLE_DELAY,
    }).throttledDragEvent;

    const throttledLineDrag = useThrottledDragEvent({
      callback: mode.eventHandlers.onLineDrag || (() => {}),
      delay: THROTTLE_DELAY,
    }).throttledDragEvent;

    const throttledMouseMove = useThrottle(
      mode.eventHandlers.onMouseMove || (() => {}),
      THROTTLE_DELAY,
    );

    // Register mouse move events
    useEffect(() => {
      if (!mode.eventHandlers.onMouseMove) return;

      const mouseMoveCallback = (e: MapMouseEvent) => throttledMouseMove(e);

      subscribe(
        "mousemove",
        "map",
        mouseMoveCallback,
        EventPriority.GEOJSON_EDITOR_HIGHEST,
      );

      return () => unsubscribe("mousemove", "map", mouseMoveCallback);
    }, [
      subscribe,
      unsubscribe,
      mode.eventHandlers.onMouseMove,
      throttledMouseMove,
    ]);

    // Register layer-specific events
    useMapEventHandler({
      layerIds: [LAYERID_EDITOR_VERTEX],
      onClick: mode.eventHandlers.onVertexClick,
      onContextMenu: mode.eventHandlers.onVertexRightClick,
      onDrag: mode.eventHandlers.onVertexDrag ? throttledVertexDrag : undefined,
      onDragEnd: mode.eventHandlers.onVertexDragEnd,
      onHover: mode.eventHandlers.onVertexHover,
      onHoverEnd: mode.eventHandlers.onVertexHoverEnd,
      priority: EventPriority.GEOJSON_EDITOR_HIGHEST,
    });

    useMapEventHandler({
      layerIds: [LAYERID_EDITOR_CENTERPOINT],
      onClick: mode.eventHandlers.onMidPointClick,
      priority: EventPriority.GEOJSON_EDITOR_HIGHEST,
    });

    useMapEventHandler({
      layerIds: [LAYERID_EDITOR_FILL],
      onClick: mode.eventHandlers.onFillClick,
      onContextMenu: mode.eventHandlers.onFillRightClick,
      onDrag: mode.eventHandlers.onFillDrag ? throttledFillDrag : undefined,
      priority: EventPriority.GEOJSON_EDITOR,
    });

    useMapEventHandler({
      layerIds: [LAYERID_EDITOR_LINE],
      onDrag: mode.eventHandlers.onLineDrag ? throttledLineDrag : undefined,
      priority: EventPriority.GEOJSON_EDITOR,
    });

    useMapEventHandler({
      onClick: mode.eventHandlers.onMapClick,
      onContextMenu: mode.eventHandlers.onMapRightClick,
      options: { registerOnMap: true },
      priority: EventPriority.GEOJSON_EDITOR,
    });

    // Register keyboard events
    useKeyPressListener(
      [
        {
          handler: wrapKeyboardEvent(mode.eventHandlers.onDelete),
          key: "Delete",
        },
        {
          handler: wrapKeyboardEvent(mode.eventHandlers.onDelete),
          key: "Backspace",
        },
        {
          handler: wrapKeyboardEvent(mode.eventHandlers.onCancel),
          key: "Escape",
        },
        {
          handler: wrapKeyboardEvent(mode.eventHandlers.onEnter),
          key: "Enter",
        },
        {
          handler: wrapKeyboardEvent(mode.eventHandlers.onControl),
          key: "Control",
        },
      ],
      map.getCanvas(),
    );

    return null;
  },
);

EventListeners.displayName = "EventListeners";
